/* eslint-disable max-lines */
import {
    AudienceRoute,
    ChannelRoute,
    ElseRoute,
    QueueRoute,
    RealmRoute,
    RegisteredAudienceRoute,
    RouteName,
    TranscriptRoute,
} from 'embed/types/routes'
import {
    EmbedEvent,
    EmbedEvents,
    RouteEvent,
} from 'embed/types/events'
import {
    composeMetadata,
    composeMetadataFromAudienceMetadata,
} from 'embed/utils/metadata'
import {
    isAsyncThunkAction,
    isFulfilled,
} from '@reduxjs/toolkit'
import {ChannelID} from 'api/types/properties'
import {NinchatEnvironmentConfig} from 'embed/types'
import {NinchatTranscriptContentsEvent} from 'api/types/events'
import {Route} from 'navigation/types'
import {config} from 'embed/listeners'
import {describeChannel} from 'api/action/describeChannel'
import {getTranscript} from 'api/action/getTranscript'
import ninchatClient from 'api/NinchatClient'
import {startAppListening} from 'middlewares/listener'

const WAIT_FOR_CHANNEL_TO_FILL_MS = 2000
const WAIT_FOR_OTHERS_TO_DESCRIBE_MS = 2000

type Effect = Parameters<typeof startAppListening>[0]['effect']
type Predicate = Parameters<typeof startAppListening>[0]['predicate']
type ListenerEffectAPI = Parameters<Effect>[1]

const getDescribedChannel = async (channel_id: ChannelID, listenerApi: ListenerEffectAPI) => {
    if (!listenerApi.getState().ninchat.user_channels[channel_id]?.channel_members) {
        if (!await listenerApi.condition((_, currentState) => !!currentState.ninchat.user_channels[channel_id]?.channel_members, WAIT_FOR_OTHERS_TO_DESCRIBE_MS)) {
            await listenerApi.dispatch(describeChannel({channel_id}))
        }
    }

    const channel = listenerApi.getState().ninchat.user_channels[channel_id]

    if (!channel?.channel_members) {
        return false
    }

    return channel
}

const composeRouteEventForTranscript = async (
    audience_id: string,
    listenerApi: ListenerEffectAPI,
    targetOrigin: string,
): Promise<TranscriptRoute | ElseRoute> => {
    if (!Object.keys(listenerApi.getState().ninchat.user_channels).length) {
        await listenerApi.condition((_, currentState) => !!Object.keys(currentState.ninchat.user_channels).length, WAIT_FOR_CHANNEL_TO_FILL_MS)
    }

    const [channel_id] = Object.entries(listenerApi.getState().ninchat.user_channels).find(([, channel]) => channel.channel_attrs.audience_id === audience_id) ?? []

    if (!channel_id) {
        return <ElseRoute>{name: RouteName.Else}
    }

    const channel = await getDescribedChannel(channel_id, listenerApi)

    if (!channel) {
        return <ElseRoute>{name: RouteName.Else}
    }

    const metadata = composeMetadata(channel.channel_members_metadata, targetOrigin)

    return {
        ...metadata.length && {metadata},
        audience_id,
        name: RouteName.Transcript,
        params: {audience_id},
    }
}

const composeRouteEventForRegisteredAudience = async (
    audience_id: string,
    listenerApi: ListenerEffectAPI,
    targetOrigin: string,
): Promise<RegisteredAudienceRoute> => {
    const base: RegisteredAudienceRoute = {
        audience_id,
        name: RouteName.RegisteredAudience,
        params: {audience_id},
    }

    try {
        const event = await listenerApi.dispatch(getTranscript({audience_id})).unwrap() as NinchatTranscriptContentsEvent

        const metadata = composeMetadataFromAudienceMetadata(event.audience_metadata, targetOrigin)
        return {
            ...base,
            ...metadata.length && {metadata},
        }
    } catch {
        return base
    }
}

let currentRealmId: string | null = null

const effect: (sendToParent: (event: EmbedEvents) => void, conf: NinchatEnvironmentConfig) => Effect = (sendToParent) => async (_action, listenerApi) => {
    listenerApi.cancelActiveListeners()

    const targetOrigin = config.get('targetOrigin')

    if (!targetOrigin) {
        console.error('missing targetOrigin')
        return
    }

    const {
        position,
        routes,
    } = listenerApi.getState().navigation

    const route = routes[position]

    if (!route) {
        console.error('missing route')
        return
    }

    let eventRoute: undefined | RouteEvent['route'] = undefined // eslint-disable-line no-undef-init

    const newRealmId = listenerApi.getState().ui.realm_id
    if (currentRealmId !== newRealmId && newRealmId) {
        eventRoute = <RealmRoute>{
            name: RouteName.Realm,
            params: {realm_id: newRealmId},
        }

        sendToParent({
            event: EmbedEvent.Route,
            route: eventRoute,
            targetOrigin,
        })
    }

    if (newRealmId) {
        currentRealmId = newRealmId
    }

    switch (route.name) {
        case 'Channel': // support Expert UI route
        case 'EmbedChannel': { // support Embed route
            const {channel_id} = (route as Route<'Channel'>).params // eslint-disable-line no-extra-parens

            if (!listenerApi.getState().ninchat.user_channels[channel_id]) {
                await listenerApi.condition((_, currentState) => !!currentState.ninchat.user_channels[channel_id], WAIT_FOR_CHANNEL_TO_FILL_MS)
            }

            const audience_id = listenerApi.getState().ninchat.user_channels[channel_id]?.channel_attrs?.audience_id

            if (!audience_id) {
                eventRoute = <ChannelRoute>{
                    name: route.name,
                    params: {channel_id},
                }
                break
            }

            const channel = await getDescribedChannel(channel_id, listenerApi)

            if (!channel) {
                eventRoute = <ElseRoute>{name: RouteName.Else}
                break
            }

            const metadata = composeMetadata(channel.channel_members_metadata, targetOrigin)

            eventRoute = <AudienceRoute>{
                ...metadata.length && {metadata},
                audience_id,
                name: route.name,
                params: {channel_id},
            }
            break
        }
        case 'Transcript': {
            const {audience_id} = (route as Route<'Transcript'>).params // eslint-disable-line no-extra-parens

            eventRoute = await composeRouteEventForTranscript(audience_id, listenerApi, targetOrigin)
            break
        }
        case 'RegisteredAudience': {
            const {audience_id} = (route as Route<'RegisteredAudience'>).params // eslint-disable-line no-extra-parens

            eventRoute = await composeRouteEventForRegisteredAudience(audience_id, listenerApi, targetOrigin)
            break
        }
        case 'Queue': // support Expert UI route
        case 'EmbedQueue': // support Embed route
            eventRoute = <QueueRoute>{
                name: route.name,
                // eslint-disable-next-line no-extra-parens
                params: {queue_id: (route as Route<'Queue'>).params.queue_id},
            }
            break
        default:
            eventRoute = <ElseRoute>{name: RouteName.Else}
    }

    sendToParent({
        event: EmbedEvent.Route,
        route: eventRoute,
        targetOrigin,
    })
}

const predicate: Predicate = (action) => {
    const {type} = action

    const test = [
        ninchatClient.connected,
        type.startsWith('NAVIGATION/'),
        isAsyncThunkAction(action) ? isFulfilled(action) : true,
    ]

    return test.every((e) => e)
}

export const start = (sendToParent: (event: EmbedEvents) => void, conf: NinchatEnvironmentConfig) => [startAppListening({
    effect: effect(sendToParent, conf),
    predicate,
})]
